import React, { useEffect, useState, useCallback } from "react";
import { Bell } from "lucide-react";
import { api } from "../lib/api";
import { requestPushPermission, showPush } from "../lib/fx";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Link } from "react-router-dom";

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [bounce, setBounce] = useState(false);
  const prevUnreadRef = React.useRef(0);
  const seenIdsRef = React.useRef(new Set());
  const firstLoadRef = React.useRef(true);

  useEffect(() => {
    requestPushPermission();
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/notifications", { params: { limit: 30 } });
      setItems(r.data);
    } catch (_) {}
  }, []);

  useEffect(() => {
    load();
    // run overdue & reminder scans in background and refresh
    Promise.all([
      api.post("/notifications/scan-overdue").catch(() => null),
      api.post("/notifications/scan-reminders").catch(() => null),
    ]).then(load);
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const unread = items.filter((i) => !i.read).length;

  useEffect(() => {
    if (unread > prevUnreadRef.current) {
      setBounce(true);
      const t = setTimeout(() => setBounce(false), 1500);
      prevUnreadRef.current = unread;
      return () => clearTimeout(t);
    }
    prevUnreadRef.current = unread;
  }, [unread]);

  useEffect(() => {
    if (firstLoadRef.current) {
      items.forEach((n) => seenIdsRef.current.add(n.notification_id));
      firstLoadRef.current = false;
      return;
    }
    items.forEach((n) => {
      if (!seenIdsRef.current.has(n.notification_id) && !n.read) {
        showPush("Doxico Studio", n.message, n.link || undefined);
      }
      seenIdsRef.current.add(n.notification_id);
    });
  }, [items]);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };
  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`relative w-9 h-9 flex items-center justify-center hover:bg-neutral-900 transition-colors ${bounce ? "animate-bounce" : ""}`}
          data-testid="notifications-bell"
        >
          <Bell className="w-4 h-4" strokeWidth={1.5} />
          {unread > 0 && (
            <span
              className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-[#00C97A] text-[10px] font-semibold text-black flex items-center justify-center rounded-full"
              data-testid="notification-count"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="end"
        className="w-96 rounded-none bg-neutral-950 border-neutral-800 p-0"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-900">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            Notifications
          </div>
          {unread > 0 && (
            <button
              onClick={markAll}
              data-testid="mark-all-read"
              className="text-[10px] uppercase tracking-[0.2em] text-[#00C97A] hover:text-white transition-colors"
            >
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && (
            <div className="px-4 py-8 text-center text-xs text-neutral-500">
              You're all caught up.
            </div>
          )}
          {items.map((n) => (
            <Link
              key={n.notification_id}
              to={n.link || "#"}
              onClick={() => {
                setOpen(false);
                if (!n.read) markRead(n.notification_id);
              }}
              data-testid={`notif-${n.notification_id}`}
              className={`block px-4 py-3 border-b border-neutral-900 hover:bg-neutral-900 transition-colors ${
                n.read ? "opacity-60" : ""
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`mt-1.5 w-1.5 h-1.5 rounded-full ${
                    n.read ? "bg-neutral-700" : "bg-[#00C97A]"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 mb-0.5">
                    {n.kind.replace(/_/g, " ")}
                  </div>
                  <div className="text-sm text-white truncate">{n.message}</div>
                  <div className="text-[10px] text-neutral-600 mt-1 font-mono">
                    {new Date(n.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
